'use client';

// T16 — MediaZoomModal: full-screen zoom/pan viewer for a single selected
// media record. Ported from the legacy media tab's MediaLightbox; no damage
// panel (that's permanently visible in MediaDetailPanel, not gated behind
// opening the zoom view). Prev/Next navigation re-added on top of T16's
// original list-column-only selection, scoped to the records passed in via
// onPrev/onNext by ClaimMediaWorkspace so the modal itself stays unaware of
// the surrounding slot/grouping logic.

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { MediaRecord } from './types';

export interface MediaZoomModalProps {
  record: MediaRecord | null;
  onClose: () => void;
  // Omitted (undefined) when there is no prev/next record to move to —
  // the modal renders that arrow disabled rather than taking a separate
  // hasPrev/hasNext flag, mirroring the onDelete/onReplace optional-callback
  // convention already used by MediaDetailPanelProps.
  onPrev?: () => void;
  onNext?: () => void;
  // "3 of 12" counter, scoped to whichever list (photos or videos) the
  // current record belongs to. Omitted when there's only one record.
  counter?: { index: number; total: number };
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;
const WHEEL_ZOOM_STEP = 0.15;

function clampZoom(value: number): number {
  return parseFloat(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value)).toFixed(2));
}

export function MediaZoomModal({ record, onClose, onPrev, onNext, counter }: MediaZoomModalProps) {
  const [zoom, setZoom] = React.useState(1);
  const [pan, setPan] = React.useState({ x: 0, y: 0 });
  const [dragging, setDragging] = React.useState(false);
  const dragOrigin = React.useRef({ mx: 0, my: 0, px: 0, py: 0 });
  const panRef = React.useRef(pan);
  const viewerRef = React.useRef<HTMLDivElement>(null);

  const isVideo = Boolean(record?.video_type);
  const recordId = record?.id;

  React.useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  // Reset zoom/pan whenever the displayed record changes.
  React.useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setDragging(false);
  }, [recordId]);

  React.useEffect(() => {
    if (!recordId) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onPrev?.();
      else if (e.key === 'ArrowRight') onNext?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [recordId, onClose, onPrev, onNext]);

  // Wheel zoom for images — non-passive so preventDefault can stop page
  // scroll. Keeps the point under the cursor visually fixed: the image is
  // centered in the viewer by default (flex items-center justify-center),
  // so the viewer's own bounding-box center is the transform's effective
  // origin, and panning by (oldZoom - newZoom) * cursorOffsetFromCenter
  // cancels out the shift scaling alone would introduce.
  React.useEffect(() => {
    const el = viewerRef.current;
    if (!el || !recordId || isVideo) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const delta = e.deltaY < 0 ? WHEEL_ZOOM_STEP : -WHEEL_ZOOM_STEP;
      setZoom((prevZoom) => {
        const nextZoom = clampZoom(prevZoom + delta);
        if (nextZoom !== prevZoom) {
          const localX = (e.clientX - centerX - panRef.current.x) / prevZoom;
          const localY = (e.clientY - centerY - panRef.current.y) / prevZoom;
          const nextPan = {
            x: panRef.current.x + (prevZoom - nextZoom) * localX,
            y: panRef.current.y + (prevZoom - nextZoom) * localY,
          };
          panRef.current = nextPan;
          setPan(nextPan);
        }
        return nextZoom;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [recordId, isVideo]);

  if (!record) return null;

  function onMouseDown(e: React.MouseEvent) {
    if (isVideo || zoom <= 1) return;
    e.preventDefault();
    dragOrigin.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
    setDragging(true);
  }
  function onMouseMove(e: React.MouseEvent) {
    if (!dragging) return;
    setPan({
      x: dragOrigin.current.px + (e.clientX - dragOrigin.current.mx),
      y: dragOrigin.current.py + (e.clientY - dragOrigin.current.my),
    });
  }
  function stopDrag() {
    setDragging(false);
  }

  return (
    <div
      data-testid="media-zoom-modal"
      className="fixed inset-0 z-50 flex flex-col bg-black select-none"
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
    >
      <div className="flex flex-shrink-0 items-center justify-between bg-black/80 px-4 py-2.5 backdrop-blur-sm">
        <div className="mr-4 min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-white">{record.file_name ?? 'Untitled'}</p>
        </div>

        <div className="flex flex-shrink-0 items-center gap-1">
          {!isVideo && (
            <>
              <button
                type="button"
                onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))}
                disabled={zoom <= MIN_ZOOM}
                title="Zoom out"
                className="rounded p-2 text-gray-300 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30"
              >
                −
              </button>
              <span className="w-10 text-center text-xs tabular-nums text-gray-300">{Math.round(zoom * 100)}%</span>
              <button
                type="button"
                onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))}
                disabled={zoom >= MAX_ZOOM}
                title="Zoom in"
                className="rounded p-2 text-gray-300 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30"
              >
                +
              </button>
              {zoom !== 1 && (
                <button
                  type="button"
                  onClick={() => {
                    setZoom(1);
                    setPan({ x: 0, y: 0 });
                  }}
                  className="rounded px-2 py-1 text-xs text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
                >
                  Reset
                </button>
              )}
            </>
          )}

          <a
            href={record.download_url}
            download={record.file_name ?? undefined}
            title="Download"
            className="rounded p-2 text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
          >
            Download
          </a>

          <button
            type="button"
            onClick={onClose}
            title="Close (Esc)"
            className="rounded p-2 text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
          >
            Close
          </button>
        </div>
      </div>

      <div
        ref={viewerRef}
        data-testid="media-zoom-viewer"
        className="relative flex flex-1 items-center justify-center overflow-hidden"
        style={{ cursor: !isVideo && zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'default' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
      >
        {onPrev && (
          <button
            type="button"
            onClick={onPrev}
            title="Previous (←)"
            aria-label="Previous"
            className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/40 p-2.5 text-white transition-colors hover:bg-black/60 sm:left-4 sm:p-3"
          >
            <ChevronLeft className="h-6 w-6" aria-hidden="true" />
          </button>
        )}
        {onNext && (
          <button
            type="button"
            onClick={onNext}
            title="Next (→)"
            aria-label="Next"
            className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/40 p-2.5 text-white transition-colors hover:bg-black/60 sm:right-4 sm:p-3"
          >
            <ChevronRight className="h-6 w-6" aria-hidden="true" />
          </button>
        )}

        {isVideo ? (
          <video
            key={record.id}
            src={record.download_url}
            controls
            className="max-h-full max-w-full rounded shadow-2xl"
            style={{ maxWidth: '92vw', maxHeight: '82vh' }}
          />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            data-testid="media-zoom-image"
            src={record.download_url}
            alt={record.file_name ?? 'Zoomed media'}
            draggable={false}
            className="rounded object-contain shadow-2xl"
            style={{
              maxWidth: '92vw',
              maxHeight: '82vh',
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transition: dragging ? 'none' : 'transform 0.15s ease',
            }}
          />
        )}

        {counter && counter.total > 1 && (
          <div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs tabular-nums text-white">
            {counter.index} / {counter.total}
          </div>
        )}
      </div>
    </div>
  );
}
